/**
 * Pure helpers for the optional drink/price report shown on the
 * post-check-in celebration screen. Extracted from CheckInCelebration.tsx
 * so the rules can be unit-tested without React/supabase/posthog.
 *
 * No I/O, no React, no side effects.
 */

// Canonical drink chips. The label is what gets written to
// check_ins.drink_ordered (except for "Other", which reveals a free-text input
// whose trimmed contents are written instead). Keep this list small — this is
// a quick optional report, not a menu.
export const DRINK_OPTIONS = [
  "Drip",
  "Latte",
  "Cappuccino",
  "Cold brew",
  "Matcha",
  "Tea",
  "Espresso",
  "Other",
] as const;
export type DrinkOption = (typeof DRINK_OPTIONS)[number];

// Mirrors the trigger's aggregation guard: drink_price >= 1 AND drink_price <= 50.
export const PRICE_MIN = 1;
export const PRICE_MAX = 50;

export type PriceBucket = "<6" | "6-9" | "9-13" | ">13";

/**
 * Bucket the reported price for analytics so we never send the raw value.
 * Buckets mirror the price_level mapping used by
 * update_cafe_price_level_from_checkins().
 */
export function bucketPrice(p: number): PriceBucket {
  if (p < 6) return "<6";
  if (p < 9) return "6-9";
  if (p < 13) return "9-13";
  return ">13";
}

/**
 * Resolve the chip selection to the literal string to write to drink_ordered.
 * For "Other", trim free-text — whitespace-only resolves to null so we never
 * write a blank value. Returns null when nothing is set.
 */
export function resolveDrinkOrder(
  selectedDrink: DrinkOption | null,
  otherDrinkText: string,
): string | null {
  if (!selectedDrink) return null;
  if (selectedDrink === "Other") {
    const trimmed = otherDrinkText.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return selectedDrink;
}

export interface PriceValidationResult {
  provided: boolean;
  valid: boolean;
  value: number | null;
}

/**
 * Validate raw price input. Empty/whitespace = omitted (valid, value null).
 * Otherwise must be finite and within [PRICE_MIN, PRICE_MAX].
 */
export function validateDrinkPrice(priceText: string): PriceValidationResult {
  const trimmed = priceText.trim();
  const provided = trimmed !== "";
  if (!provided) {
    return { provided: false, valid: true, value: null };
  }
  const parsed = Number(trimmed);
  const valid =
    Number.isFinite(parsed) && parsed >= PRICE_MIN && parsed <= PRICE_MAX;
  return { provided: true, valid, value: valid ? parsed : null };
}

/**
 * Build the partial update for check_ins. Returns null if nothing to save.
 */
export function buildDrinkPatch(
  resolvedDrink: string | null,
  validPrice: number | null,
): { drink_ordered?: string; drink_price?: number } | null {
  const patch: { drink_ordered?: string; drink_price?: number } = {};
  if (resolvedDrink) patch.drink_ordered = resolvedDrink;
  if (validPrice !== null) patch.drink_price = validPrice;
  return Object.keys(patch).length === 0 ? null : patch;
}

/**
 * Map chip selection to analytics category. "Other" → "other" (never the raw
 * free-text). null when nothing selected.
 */
export function orderCategory(selectedDrink: DrinkOption | null): string | null {
  if (!selectedDrink) return null;
  if (selectedDrink === "Other") return "other";
  return selectedDrink;
}
